<template>
  <div>
    <CModal
      :show.sync="showDualLoginModal"
      :closeOnBackdrop="false"
      centered
      size="sm"
    >
      <template #header>
        <h5 class="mb-0">Active Session Detected</h5>
      </template>
      <div class="p-2">
        You are already logged in on <strong>{{ existingDevice }}</strong>. Do you want to logout that session?
      </div>
      <template #footer>
        <CButton color="secondary" @click="onCancelDualLogin">No</CButton>
        <CButton color="primary" @click="onConfirmDualLogin">Yes</CButton>
      </template>
    </CModal>

    <section class="main mt-5">
      <div class="d-flex flex-column flex-md-row mb-5">
        <div class="col-12 col-md-6">
          <h1 class="f-secondary text-gradient mb-5">Why TalentFind?</h1>
          <ul class="m-0">
            <li class="list-icon mb-2">
              Find thousands of relevant roles at your fingertips
            </li>
            <li class="list-icon mb-2">
              Get guidance through your healthcare training pathway
            </li>
            <li class="list-icon mb-2">
              Access accredited language training, upskilling and E-learning
              opportunities
            </li>
            <li class="list-icon mb-2">Build global healthcare connections</li>
            <li class="list-icon mb-2">Develop your career in healthcare</li>
            <li class="list-icon mb-2">
              Be part of a global healthcare community
            </li>
          </ul>
        </div>
        <div class="col-12 col-md-6">
          <div class="card-bg" id="register-form">
            <h2 class="f-secondary font-40 mb-5 text-gradient-2">
              Join the global health careers marketplace today
            </h2>

            <div class="custom-tabs mb-2">
              <a
                href="#"
                class="custom-tab"
                :class="{ active: activeTab === 'login' }"
                @click.prevent="activeTab = 'login'"
                >Login</a
              >
              <a
                href="#"
                class="custom-tab"
                :class="{ active: activeTab === 'register' }"
                @click.prevent="activeTab = 'register'"
                >Register</a
              >
            </div>

            <div v-if="activeTab === 'login'">
              <div class="login-box">
                <ValidationObserver ref="loginForm" v-slot="{ invalid }">
                  <form @submit.prevent="submitLoginForm">
                    <!-- Email Input -->
                    <div class="mb-3">
                      <label for="email" class="form-label"
                        >Email Address*</label
                      >
                      <ValidationProvider
                        rules="required|email"
                        v-slot="{ errors }"
                        name="email"
                      >
                        <input
                          type="email"
                          v-model="login_form.email"
                          id="email"
                          autocomplete="off"
                          class="custom-input"
                        />
                        <span class="text-danger small">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </div>

                    <!-- Password Input -->
                    <div class="mb-3">
                      <label for="password" class="form-label"
                        >Password*</label
                      >
                      <ValidationProvider
                        rules="required"
                        v-slot="{ errors }"
                        name="password"
                      >
                        <input
                          type="password"
                          v-model="login_form.password"
                          id="password"
                          autocomplete="new-password"
                          class="custom-input"
                        />
                        <span class="text-danger small">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </div>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <!-- Submit Button -->
                      <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="isLoading"
                      >
                        <span
                          v-if="isLoading"
                          class="spinner-border spinner-border-sm"
                        ></span>
                        <span v-else>Login</span>
                      </button>
                      <!-- Forget password  -->
                      <div class="text-right">
                        <a
                          class="text-white bold cursor-pointer"
                          @click.prevent="redirectToForgot"
                        >
                          Forgot password?
                        </a>
                      </div>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>

            <div v-if="activeTab === 'register'">
              <div v-if="showBasicQuestions">
                <ValidationObserver ref="registerForm" v-slot="{ invalid }">
                  <form @submit.prevent="registerForm">
                    <!-- Email Input -->
                    <label for="email">Email*</label>
                    <ValidationProvider
                      rules="required|email"
                      v-slot="{ errors }"
                      name="email"
                    >
                      <input
                        type="email"
                        v-model="user.email"
                        id="email"
                        disabled
                      />
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Confirm Email Input -->
                    <label for="confirmEmail">Confirm Email*</label>
                    <ValidationProvider
                      rules="required|email|confirmed:email"
                      v-slot="{ errors }"
                      name="Confirm Email"
                    >
                      <input
                        type="email"
                        v-model="user.confirmEmail"
                        id="confirmEmail"
                        disabled
                      />
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Title Dropdown -->
                    <label for="title">Title*</label>
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="Title"
                    >
                      <select v-model="user.title" id="title" class="">
                        <option
                          v-for="title in titles"
                          :key="title.code"
                          :value="title"
                        >
                          {{ title.label }}
                        </option>
                      </select>
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <label for="firstname">First Name*</label>
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="First Name"
                    >
                      <input
                        type="text"
                        id="firstname"
                        name="firstname"
                        v-model="user.firstName"
                      />
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <label for="surname">Last Name*</label>
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="Surname"
                    >
                      <input
                        type="text"
                        id="surname"
                        name="surname"
                        v-model="user.surName"
                      />
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Phone Number with Country Code -->
                    <label for="phone">Mobile Phone Number*</label>
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="Phone Number"
                    >
                      <div class="d-flex">
                        <select v-model="user.dialingCode" class="w-25">
                          <option
                            v-for="(code, index) in dialingCodeWithCountryCode"
                            :key="index"
                            :value="code.dialing_code"
                          >
                            {{ code.label }}
                          </option>
                        </select>
                        <input
                          type="number"
                          v-model="user.phoneNumber"
                          id="phone"
                          class="w-75"
                        />
                      </div>
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Profession Dropdown -->
                    <label for="profession">Profession*</label>
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="Profession"
                    >
                      <select
                        v-model="user.profession"
                        id="profession"
                        class=""
                      >
                        <option
                          v-for="profession in candidateType"
                          :key="profession.code"
                          :value="profession"
                        >
                          {{ profession.label }}
                        </option>
                      </select>
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>
                    <label for="specialty">Specialty/Expertise*</label>
                    <!-- <ValidationProvider rules="required" v-slot="{ errors }" name="Specialty">
                      <select v-model="user.specialty" id="specialty" class="">
                        <div v-if="user.profession.code == 2">
                          <option
                            v-for="(specialty, index) in getNurseSubSpecialty"
                            :key="index"
                            :value="specialty"
                          >
                            {{ specialty.label }}
                          </option>
                        </div>
                        <div v-else>
                          <option
                            v-for="(specialty, index) in specialties"
                            :key="index"
                            :value="specialty"
                          >
                            {{ specialty.label }}
                          </option>
                        </div>
                      </select>
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider> -->
                    <ValidationProvider
                      rules="required"
                      v-slot="{ errors }"
                      name="Specialty"
                    >
                      <select v-model="user.speciality" id="specialty" class="">
                        <option
                          v-for="(speciality, index) in user.profession?.code ==
                          2
                            ? getNurseSubSpecialty
                            : specialties"
                          :key="index"
                          :value="speciality"
                        >
                          {{ speciality.label }}
                        </option>
                      </select>
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Password -->
                    <label for="password">Password*</label>
                    <ValidationProvider
                      name="password"
                      :rules="{
                        required: true,
                        password_length: 8,
                        password_strength: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
                      }"
                      v-slot="{ errors }"
                    >
                      <div class="password-input-wrapper">
                        <input
                          :type="showPassword ? 'text' : 'password'"
                          v-model="user.password"
                          id="password"
                          class="password-input"
                          autocomplete="new-password"
                        />
                        <button
                          type="button"
                          class="password-toggle-btn"
                          @click="showPassword = !showPassword"
                          tabindex="-1"
                        >
                          <i
                            :class="
                              showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'
                            "
                          ></i>
                        </button>
                      </div>
                      <div class="text-danger small">{{ errors[0] }}</div>
                    </ValidationProvider>

                    <!-- Confirm Password -->
                    <label for="confirmPassword">Confirm Password*</label>
                    <ValidationProvider
                    rules="required|confirmed:password"
                      v-slot="{ errors }"
                      name="confirmPassword"
                    >
                      <div class="password-input-wrapper">
                        <input
                          :type="showConfirmPassword ? 'text' : 'password'"
                          v-model="user.confirmPassword"
                          id="confirmPassword"
                          class="password-input"
                          autocomplete="new-password"
                        />
                        <button
                          type="button"
                          class="password-toggle-btn"
                          @click="showConfirmPassword = !showConfirmPassword"
                          tabindex="-1"
                        >
                          <i
                            :class="
                              showConfirmPassword
                                ? 'fas fa-eye-slash'
                                : 'fas fa-eye'
                            "
                          ></i>
                        </button>
                      </div>
                      <div class="text-danger small">{{ errors[0] }}</div>
                    </ValidationProvider>

                    <label for="is_student"
                      >What Is Your Highest Level of Education or
                      Training?*</label
                    >
                    <ValidationProvider rules="required" v-slot="{ errors }">
                      <div class="form-group">
                        <div
                          class="form-check d-flex align-items-start mb-2"
                          v-for="(option, index) in educationOptions"
                          :key="index"
                        >
                          <input
                            type="radio"
                            :id="'education_option_' + index"
                            v-model="user.is_student"
                            :value="option.code"
                            name="is_student"
                            class="form-check-input mt-1 align-self-start"
                          />
                          <label
                            :for="'education_option_' + index"
                            class="form-check-label ms-2"
                          >
                            {{ option.label }}
                          </label>
                        </div>

                        <span class="text-danger">{{ errors[0] }}</span>
                      </div>
                    </ValidationProvider>

                    <!-- Submit Button -->
                    <div class="d-flex">
                      <button
                        type="button"
                        class="btn-primary btn custom-color mr-3"
                        @click="$router.push('/tfm')"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="isLoading"
                      >
                        <span v-if="isLoading" class="spinner"></span>
                        <span v-else>Register Now</span>
                      </button>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
              <div v-else>
                <ValidationObserver ref="loginForm" v-slot="{ invalid }">
                  <form @submit.prevent="submitForm">
                    <!-- Email Input -->
                    <label for="email">Email*</label>
                    <ValidationProvider
                      rules="required|email"
                      v-slot="{ errors }"
                      name="email"
                    >
                      <input type="email" v-model="user.email" id="email" />
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Confirm Email Input -->
                    <label for="confirmEmail">Confirm Email*</label>
                    <ValidationProvider
                      rules="required|email|confirmed:email"
                      v-slot="{ errors }"
                      name="Confirm Email"
                    >
                      <input
                        type="email"
                        v-model="user.confirmEmail"
                        id="confirmEmail"
                      />
                      <span class="error-text">{{ errors[0] }}</span>
                    </ValidationProvider>

                    <!-- Submit Button -->
                    <div class="d-flex">
                      <button
                        type="submit"
                        class="btn btn-primary"
                        :disabled="isLoading"
                      >
                        <span v-if="isLoading" class="spinner"></span>
                        <span v-else>Next</span>
                      </button>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Modal 1 -->
      <Modal v-if="showModal.modal1" @close="showModal.modal1 = false">
        <div class="line-height-40">
          <div class="text-center">
            <h1 class="f-secondary text-gradient">
              Welcome, {{ existUserData.first_name | firstLetterCaps }}
            </h1>
            <h5 class="bold">Start Your Journey with Us Today!</h5>
            <h5 class="bold">
              Complete your profile to unlock a world of opportunities:
            </h5>
          </div>
          <ul>
            <li>Access Your Personalised Portal</li>
            <li>
              Manage and update your profile, preferences, CV, and
              documentation.
            </li>
            <li>Explore accredited training.</li>
            <li>
              View and apply for the latest job openings tailored to your
              profile.
            </li>
            <li>Receive notifications, updates, and feedback in one place.</li>
            <li>Maintain an optional private or public profile.</li>
            <li>Access the TalentFind Marketplace.</li>
          </ul>
          <h5 class="bold">with more to come...</h5>
          <div>
            <button class="btn btn-primary mr-4" @click="registerLater()">
              I'LL FINISH IT LATER
            </button>
            <button class="btn btn-primary" @click="closeModal('modal1')">
              COMPLETE PROFILE
            </button>
          </div>
        </div>
      </Modal>

      <!-- Modal 2 -->
      <Modal v-if="showModal.modal2" @close="showModal.modal2 = false">
        <div class="text-left line-height-40">
          <h1 class="f-secondary text-gradient">
            Welcome back, {{ existUserData.first_name | firstLetterCaps }}
          </h1>
          <h5 class="bold">
            Your profile details are already registered with TalentFind World, a
            member of the TalentFind Marketplace.
          </h5>
          <div>
            <button class="btn btn-primary mr-5" @click="closeModal('modal2')">
              Register Now
            </button>
          </div>
        </div>
      </Modal>

      <!-- Modal 3 -->
      <Modal v-if="showModal.modal3" @close="showModal.modal3 = false">
        <div class="text-left line-height-40">
          <h1 class="f-secondary text-gradient">
            Welcome, {{ existUserData.first_name | firstLetterCaps }}
          </h1>
          <h5 class="bold">
            An email will be sent to you shortly with a link to complete your
            registration.
          </h5>
        </div>
      </Modal>

      <!-- Modal 4 -->
      <Modal v-if="showModal.modal4" @close="showModal.modal4 = false">
        <div class="text-left line-height-40">
          <h1 class="f-secondary text-gradient">
            Welcome back, {{ existUserData.candidate_name | firstLetterCaps }}
          </h1>
          <h5 class="bold">
            You are already registered with TalentFind Marketplace. Please click
            "Login" to access your account.
          </h5>
          <div>
            <button class="btn btn-primary mr-5" @click="closeModal('modal4')">
              Login
            </button>
          </div>
        </div>
      </Modal>

      <!-- Password Modal -->
      <Modal
        v-if="passwordModal.isShowPopup"
        @close="passwordModal.isShowPopup = false"
      >
        <form @submit.prevent="registerWithPassword">
          <div class="text-left">
            <h2 class="text-gradient">
              Welcome back,
              {{
                (existUserData.first_name || getExistUserCandidate.first_name)
                  | firstLetterCaps
              }}
            </h2>

            <div class="w-75">
              <label for="password">Enter Password*</label>
              <ValidationProvider rules="required" v-slot="{ errors }" name="password">
                <input
                  id="password"
                  style="border: 2px solid black"
                  type="password"
                  v-model="passwordModal.data.password"
                />
                <span class="error-text">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>

            <CButton color="link" @click="redirectToForgot" class="p-0 forgot-link">
              Forgot password?
            </CButton>

            <div class="text-right">
              <button type="submit" class="btn btn-primary mr-5">
                Register Now
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </section>
  </div>
</template>

<script>
import { required, email, confirmed, regex, min, numeric, alpha_spaces } from "vee-validate/dist/rules";
import { extend } from "vee-validate";
import { mapActions, mapGetters } from "vuex";
import {
  redirectToRegistrationUrl,
  getLocalOrgDetail,
} from "../../helpers/helper";

// Extend validation rules
extend("required", { ...required, message: "This field is required" });
extend("email", { ...email, message: "Invalid email address" });
extend("confirmed", {
  ...confirmed,
  message: "Email and confirm email should be same",
});
extend("password_length", {
  validate: (value) => {
    if (typeof value !== "string") return false;
    return value.length >= 8;
  },
  message: "Password must be at least 8 characters",
});
extend("password_strength", {
  ...regex,
  message: "Password must have capitals, numbers and special characters",
})

extend("confirmed", {
  ...confirmed,
  message: "This field should match password",
});
import Modal from "./ModalComponent.vue";

export default {
  components: {
    Modal,
  },
  watch: {
    "user.email"(newVal) {
      if (newVal) {
        this.user.email = newVal.trim().toLowerCase();
      }
    },
    "user.confirmEmail"(newVal) {
      if (newVal) {
        this.user.confirmEmail = newVal.trim().toLowerCase();
      }
    },
    "login_form.email"(newVal) {
      if (newVal) {
        this.login_form.email = newVal.trim().toLowerCase();
      }
    },
    "user.profession"(newProfession) {
      if (newProfession) {
        this.specialties;
      }
    },
  },
  data() {
    return {
      user: {
        campaignName: "Main Registration",
        email: "",
        confirmEmail: "",
        title: "",
        dialingCode: "",
        phoneNumber: "",
        firstName: "",
        surName: "",
        profession: "",
        password: "",
        confirmPassword: "",
      },
      isLoading: false,
      showBasicQuestions: false,
      existUserData: {},
      showModal: {
        modal1: false,
        modal2: false,
        modal3: false,
        modal4: false,
      },
      activeTab: "login",
      login_form: {
        email: "",
        password: "",
      },
      showPassword: false,
      showConfirmPassword: false,
      existUserCandidate: {},
      passwordModal: {
        isShowPopup: false,
        data: {},
      },
      showDualLoginModal: false,
      existingDevice: "",
    };
  },
  computed: {
    ...mapGetters([
      "dialingCodeWithCountryCode",
      "titles",
      "candidateType",
      "getPathAcronymByOrgDetail",
      "specialityByTypeAndCountry",
      "getNurseSubSpeciality",
      "getExistUserCandidate",
    ]),
    specialties() {
      let specialties =
        this.specialityByTypeAndCountry({
          candidateType: this.user.profession?.code,
        }) || [];
      return specialties;
    },
    educationOptions() {
      return [
        {
          code: true,
          label:
            "Below Degree Level, Currently Studying, or No Formal Qualifications",
        },
        { code: false, label: "Degree Level or Higher" },
      ];
    },
    getNurseSubSpecialty() {
      return this.getNurseSubSpeciality || [];
    },
  },
  methods: {
    ...mapActions([
      "userExistInCampaign",
      "initQuickRegisterFetchOptions",
      "campaignRegistration",
      "campaignRegisterLater",
      "checkCandidateExistInCampaignPreReg",
      "isEmailBelongsToAcronym",
      "login",
      "createShortRegistration",
      "getRegisteredHospitalsByEmail",
      "loginBeforeRegistration",
      "showLoader",
      "hideLoader",
      "confirmDualLogin",
      "cancelDualLogin",
    ]),
    async submitForm() {
      const isValid = await this.$refs.loginForm.validate();
      if (isValid) {
        this.isLoading = true;
        const data = {
          campaign_name: this.user.campaignName,
          email: this.user.email.trim().toLowerCase(),
        };
        try {
          const { customer_uid, organisation_id } = getLocalOrgDetail();
          const getRegisteredHospitalsByEmailPayload = {
            email: this.user.email.trim().toLowerCase(),
            customer_uid: customer_uid,
            organisation_uid: organisation_id,
          };

          const exist_user_candidate = await this.getRegisteredHospitalsByEmail(
            getRegisteredHospitalsByEmailPayload
          );
          if (exist_user_candidate?.data?.full_registration === false) {
            const data = exist_user_candidate.data;
            this.$store.commit("SET_EXIST_USER_CANDIDATE", data);

            const candidate_registered_org_ids =
              data?.customer_organisation_candidates?.map(
                (v) => v.organisation_id
              ) || [];

            if (
              candidate_registered_org_ids.length > 0 &&
              candidate_registered_org_ids.includes(organisation_id)
            ) {
              this.passwordModal.isShowPopup = true;
            } else {
              this.$store.commit("SET_CURRENT_DISPLAY_PAGE", 5);
              this.$router.push({
                path: redirectToRegistrationUrl(data.email),
              });
            }
            return;
          }

          const response = await this.checkCandidateExistInCampaignPreReg(
            data.email
          );
          if (
            response?.data?.is_pre_reg_candidate ||
            response?.data?.is_exist_user
          ) {
            this.existUserData = {
              ...response.data,
            };
            this.$router.push({
              path: redirectToRegistrationUrl(data.email),
              query: { welcome_modal: "true" },
            });
          } else {
            this.showBasicQuestions = true;
          }
        } catch (error) {
          console.error("Error:", error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async registerForm() {
      const isValid = await this.$refs.registerForm.validate();
      if (isValid) {
        this.isLoading = true;
        try {
          const data = {
            first_name: this.user?.firstName,
            surname: this.user?.surName,
            email: this.user.email.trim().toLowerCase(),
            phone: this.user.dialingCode + "^" + this.user.phoneNumber,
            candidate_type_id: this.user?.profession?.code,
            title_id: this.user?.title?.code,
            speciality_id: this.user?.speciality?.code,
            is_student: this.user?.is_student,
            sub_speciality_id: this.user.speciality?.sub_speciality_id || null,
            password: this.user?.password,
          };

          const response = await this.createShortRegistration(data);
          if (response.user_id) {
            this.existUserData.first_name = this.user.firstName;
            this.existUserData = {
              ...this.existUserData,
              ...response.data,
            };
            this.showModal.modal1 = true;
          }
          // Only for TFM set `displayPage` in `Registration.vue`
          this.$store.commit("SET_CURRENT_DISPLAY_PAGE", 4);
          this.showBasicQuestions = false;
        } catch (error) {
          console.error("Error:", error);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async closeModal(modal_name) {
      if (modal_name == "modal1") {
        const data = {
          username: this.user.email,
          password: this.user.password,
          is_registration_page: true,
        };
        this.showLoader();
        await this.loginForRegistration(data);
        this.hideLoader();
        this.showModal[modal_name] = false;
        return;
      }

      window.location.href = redirectToRegistrationUrl(
        this.user.email.trim().toLowerCase()
      );
    },
    async registerLater() {
      await this.campaignRegisterLater({
        email: this.user.email,
        campaign_name: this.user.campaignName,
      });
      this.showModal.modal1 = false;
      this.clearFields();
    },
    async submitLoginForm() {
      try {
        const isValid = await this.$refs.loginForm.validate();
        if (!isValid) {
          return;
        }

        this.isLoading = true;

        const data = {
          username: this.login_form?.email,
          password: this.login_form?.password,
        };

        const res = await this.isEmailBelongsToAcronym(data);

        if (res?.status === 200) {
          const loginRes = await this.login(data);
          if (loginRes?.has_existing_session) {
            this.existingDevice = loginRes.existing_device || "Another Device";
            this.showDualLoginModal = true;
          } 
        } 
      } catch (error) {
        console.error("[TFM Login] Login error:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async onConfirmDualLogin() {
      this.showDualLoginModal = false;
      await this.confirmDualLogin();
    },
    onCancelDualLogin() {
      this.showDualLoginModal = false;
      this.cancelDualLogin();
    },
    redirectToForgot() {
      this.$router.push({
        path: `/${this.getPathAcronymByOrgDetail}/forgot`,
      });
    },
    async loginForRegistration(data) {
      try {
        const res = await this.login({
          username: data.username,
          password: data.password,
          is_registration_page: data.is_registration_page,
        });

        if (res.status === 200) {
          await this.$router.push({
            path: redirectToRegistrationUrl(data.username),
          });
        }
      } catch (error) {
        console.error("Login error:", error);
      }
    },
    async registerWithPassword() {
      const login_data = {
        username: this.user.email,
        password: this.passwordModal.data.password,
      };

      this.$store.commit("SET_CURRENT_DISPLAY_PAGE", 4);

      if (login_data?.username && login_data?.password) {
        await this.loginForRegistration(login_data);
      }
    },
    redirectToForgot() {
      this.$router.push({
        path: `/${this.getPathAcronymByOrgDetail}/forgot`,
      });
    },
    clearFields() {
      this.login_form = {
        email: "",
        password: "",
      };
      this.user = {
        email: "",
        confirmEmail: "",
        title: "",
        dialingCode: "",
        phoneNumber: "",
        firstName: "",
        surName: "",
        profession: "",
      }
      this.$refs.registerForm?.reset();
      this.$refs.loginForm?.reset();
    }
  },
  mounted() {
    this.initQuickRegisterFetchOptions();
  },
  beforeDestroy() {
    this.user = {
      campaignName: "Main Registration",
      email: "",
      confirmEmail: "",
      title: "",
      dialingCode: "",
      phoneNumber: "",
      firstName: "",
      surName: "",
      profession: "",
    };
    this.isLoading = false;
    this.showBasicQuestions = false;
    this.existUserData = {};
  },
  filters: {
    firstLetterCaps(data) {
      if ((data == "") | (data == null)) return "";
      return data.charAt(0).toUpperCase() + data.slice(1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../src/assets/scss/stm.scss";
.card-bg {
  min-height: 600px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .password-input {
  width: 100%;
  padding-right: 40px;
}

.password-toggle-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  color: #6c757d;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  bottom: 24px;
}

.password-toggle-btn:hover {
  color: #495057;
}

.password-toggle-btn:focus {
  outline: none;
}

.forgot-link:hover {
  color: blue !important;
}
</style>

<style>
.modal-footer .btn-primary,
.modal-footer .btn-secondary {
  padding: 0.375rem 0.75rem !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  min-width: unset !important;
  width: auto !important;
  border-radius: 0.25rem !important;
  margin-top: 0 !important;
}
</style>
